import { useRef } from 'react'

import { UseFormMethods } from 'react-hook-form'

import { Box, Flex, ScaleFade } from '@chakra-ui/react'

import {
  FormDatePickerControl,
  FormInputSelectControl,
  FormRadioInputControl,
  FormTextareaControl,
} from '@/components/ui'
import { Action } from '@/graphql/generated/schemas'

import { AiOperatorCriteria } from '../ai-operator-criteria/components/AiOperatorCriteria'
import { FormInputsT, RuleInputDataIProps } from '../types/types'
import { MAX_RULENAME_LENGTH, actionOptions } from '../utils/utils'
import { CrossDayAlert } from './CrossDayAlert'
import { EmailInputSelectControl } from './EmailInputSelectControl'
import { LabeledSwitch } from './LabeledSwitch'
import { RecurrenceToggle } from './RecurrenceToggle'
import { RuleScheduleAlert } from './RuleScheduleAlert'
import { RuleSection } from './RuleSection'

export interface IncidentRuleViewIProps {
  rulesData: RuleInputDataIProps
  form: UseFormMethods<FormInputsT>
}

export const IncidentRuleView = ({
  rulesData,
  form,
}: IncidentRuleViewIProps) => {
  const deviceRef = useRef<HTMLDivElement>(null)
  const {
    days,
    emailOptions,
    endDate,
    endTimeDate,
    facilityName,
    repeatIntervalNum,
    shouldRepeat,
    shouldShowDays,
    shouldShowRecipients,
    shouldEnd,
    startTime,
    timeZone,
    isLoading,
    isDeviceLoading,
    isRecipientsLoading,
    aiOperatorResponseRules,
    allDayOverride: isAllDay,
  } = rulesData
  const { control, register, watch } = form
  const watchAction = watch('action')

  const isAiOperatorActionSelected =
    watchAction?.value === Action.EnableAiOperator

  return (
    <Flex direction='column' h='calc(100vh - 85px)'>
      <Flex
        direction='row'
        gap={{ base: '2px', md: '2px', lg: '6px' }}
        h='100%'
        overflowY='scroll'
        py='20px'
        w='full'
      >
        {/* Column 1: Name, Facilities, Floors, Devices */}
        <Flex
          direction='column'
          flex={{ base: '1', lg: '0.9' }}
          px={{ base: '16px', md: '24px' }}
        >
          <RuleSection mb={6}>
            <FormTextareaControl
              id='name'
              isDisabled={true}
              label='Name of rule'
              labelColour='#1A202C'
              labelFontWeight='600'
              labelOpacity={1}
              maxCharCount={MAX_RULENAME_LENGTH}
              placeholder='Enter a name for this rule'
              textareaRef={register({
                maxLength: {
                  value: MAX_RULENAME_LENGTH,
                  message: `Rule name should have less than ${MAX_RULENAME_LENGTH} characters`,
                },
                required: 'Rule name is required',
              })}
            />
          </RuleSection>

          <RuleSection label='Select the devices associated with the rule'>
            <Box mb={6}>
              <FormInputSelectControl
                control={control}
                defaultValue={null}
                id='facility'
                isClearable
                isDisabled={true}
                isLoading={isDeviceLoading}
                isSearchable
                label='Facility'
                labelColour='#1A202C'
                labelFontWeight='600'
                labelOpacity={1}
                options={[]}
                rules={{ required: true }}
              />
            </Box>
            <Box mb={6}>
              <FormRadioInputControl
                control={control}
                dropdownContainerHeight='200px'
                id='floor'
                isDisabled={true}
                isLoading={isDeviceLoading}
                label='Floor'
                leftRadioButtonLabel='All floors'
                options={[]}
                rightRadioButtonLabel='Select one or more floors'
              />
            </Box>
            <Box ref={deviceRef}>
              <FormRadioInputControl
                control={control}
                dropdownContainerHeight='200px'
                id='device'
                isDisabled={true}
                isLoading={isDeviceLoading}
                label='Door Device'
                leftRadioButtonLabel='All devices'
                options={[]}
                rightRadioButtonLabel='Select one or more devices'
              />
            </Box>
          </RuleSection>
        </Flex>

        {/* Column 2: Event, Actions Schedule */}
        <Flex
          direction='column'
          flex={{ base: '1', lg: '1.1' }}
          px={{ base: '16px', md: '24px' }}
        >
          <RuleSection label='Rule Actions' mb={6}>
            <Box mb={6}>
              <FormInputSelectControl
                control={control}
                id='action'
                isClearable
                isDisabled={true}
                isLoading={isLoading}
                isSearchable
                label='Choose Action'
                labelColour='#1A202C'
                labelFontWeight='600'
                labelOpacity={1}
                options={actionOptions}
                rules={{ required: true }}
              />
            </Box>

            <EmailInputSelectControl
              control={control}
              id='emails'
              isDisabled
              isLoading={isRecipientsLoading}
              isOpen={shouldShowRecipients}
              label='Recipients'
              options={emailOptions}
              placeholder='Add Recipients'
            />

            {isAiOperatorActionSelected && (
              <ScaleFade
                in={true}
                initialScale={0.95}
                transition={{ enter: { duration: 0.3 } }}
              >
                <AiOperatorCriteria
                  disabled={true}
                  previewData={aiOperatorResponseRules}
                />
              </ScaleFade>
            )}
            <FormRadioInputControl
              control={control}
              id='event'
              isDisabled={true}
              label='Event'
              leftRadioButtonLabel='All events'
              options={[]}
              rightRadioButtonLabel='Select one or more events'
            />
          </RuleSection>

          <RuleSection label='Select time frame for the action to be applied'>
            <RuleScheduleAlert
              facilityName={facilityName}
              timeZone={timeZone}
            />

            <CrossDayAlert endTime={endTimeDate} startTime={startTime} />

            <Box mb={6}>
              <LabeledSwitch
                id='all-day-switch'
                inputRef={null}
                isChecked={isAllDay}
                isDisabled
                label='All Day'
              />
              <FormDatePickerControl
                control={control}
                id='date'
                isAllDay={isAllDay}
                isDisabled
                selectedDate={startTime}
                selectedEndTime={endTimeDate}
                showEndTimePicker
                showStartTimePicker
                timeZone={timeZone}
              />
            </Box>

            <RecurrenceToggle
              control={control}
              days={days}
              endDate={endDate}
              isDisabled={true}
              repeatIntervalNum={repeatIntervalNum}
              selectedStartDate={startTime}
              shouldEnd={shouldEnd}
              shouldRepeat={shouldRepeat}
              shouldShowDays={shouldShowDays}
            />
          </RuleSection>
        </Flex>
      </Flex>
    </Flex>
  )
}
